/**
 * The collapsed/expanded sidebar of this reader, on this machine: the choice lives in
 * localStorage (never on the server), the verdicts are pure in `utils/navRail.ts`, and this file
 * only does the storage — the same split as `useTheme` (rule S1).
 *
 * ⚠️ Reading or writing localStorage throws in a locked-down browser profile. Both sides are
 * wrapped: the sidebar then opens expanded every time, which is the safe way to be wrong.
 */
import { useCallback, useEffect, useState } from 'react';

import {
  DEFAULT_NAV_RAIL,
  NAV_RAIL_STORAGE_KEY,
  type NavRail,
  navRailFromStorage,
  navRailToStorage,
} from '../utils/navRail.js';

/** The stored choice of this reader — the default when storage is unreadable. */
export function readStoredNavRail(): NavRail {
  try {
    return navRailFromStorage(localStorage.getItem(NAV_RAIL_STORAGE_KEY));
  } catch {
    return DEFAULT_NAV_RAIL;
  }
}

export function useNavRail() {
  const [rail, setRail] = useState<NavRail>(readStoredNavRail);

  useEffect(() => {
    try {
      localStorage.setItem(NAV_RAIL_STORAGE_KEY, navRailToStorage(rail));
    } catch {
      /* A browser that refuses storage still gets the sidebar it can see. */
    }
  }, [rail]);

  const toggle = useCallback(() => {
    setRail((current) => !current);
  }, []);

  return { rail, toggle };
}
